<template>
    <div class="d-flex flex-column flex-grow-1 align-items-center justify-content-center full-height">
        <img :src="$getSetting('assets.logo')" alt="" class="cms-login-form__logo">
        <form class="ak-panel ak-panel--no-padding cms-login-form" @submit.prevent="nextStep">
            <div class="ak-panel__body">
                <ak-loader :loading="loading" :lightOverlay="true"/>

                <h1 class="text--center">{{ $t('Aanmelden') }}</h1>

                <div v-if="ssoFailed" class="ak-form__error m-b-xs-10">
                    {{ $t('Aanmelden mislukt. Contacteer je IT verantwoordelijke of gebruik je e-mailadres.')}}
                </div>

                <div v-if="storageWarning && step === 'email'" class="ak-form__error m-b-xs-10">
                    {{ storageWarning }}
                </div>

                <transition-group
                    name="slide-left"
                    class="cms-login-form__main"
                    @after-enter="focusField"
                >
                    <div
                        v-if="step === 'email'"
                        key="email"
                        class="cms-login-form__step"
                    >
                        <div v-if="showAccountChooser" class="warehouse-login-accounts">
                            <div
                                v-for="account in savedAccounts"
                                :key="account.email"
                                class="warehouse-login-accounts__account"
                            >
                                <button
                                    type="button"
                                    class="warehouse-login-accounts__identity"
                                    @click="loginSavedAccount(account)"
                                >
                                    <span class="warehouse-login-accounts__name">{{ account.displayName }}</span>
                                    <span class="warehouse-login-accounts__email">{{ account.email }}</span>
                                </button>
                                <button
                                    type="button"
                                    class="warehouse-login-accounts__remove"
                                    :aria-label="$t('Verwijderen van dit toestel')"
                                    :title="$t('Verwijderen van dit toestel')"
                                    @click="removeSavedAccount(account)"
                                >
                                    <i class="ak-icon">delete</i>
                                </button>
                            </div>

                            <div class="warehouse-login-accounts__actions">
                                <ak-link
                                    :disableBackground="true"
                                    @click="useAnotherAccount"
                                >
                                    {{ $t('Aanmelden met een ander account') }}
                                </ak-link>
                            </div>
                        </div>

                        <template v-else>
                            <ak-input-text
                                ref="email"
                                id="email"
                                v-model="values.email"
                                type="email"
                                :hasError="errorMessage !== null"
                                :error="errorMessage"
                                :title="$t('E-mailadres')"
                            />

                            <div class="ak-button-group">
                                <ak-button
                                    :full-width="true"
                                    @click="nextStep"
                                >
                                    {{ $t('Ga verder') }}
                                </ak-button>
                            </div>

                            <div v-if="savedAccounts.length" class="warehouse-login-accounts__actions">
                                <ak-link
                                    :disableBackground="true"
                                    @click="showSavedAccounts"
                                >
                                    {{ $t('Terug naar opgeslagen accounts') }}
                                </ak-link>
                            </div>
                        </template>
                    </div>

                    <div
                        v-if="step === 'password'"
                        key="password"
                        class="cms-login-form__step"
                    >
                        <div class="cms-login-form__email">
                            <ak-button
                                :disableBackground="true"
                                size="small"
                                @click="goToStep('email')"
                            >
                                <i class="ak-icon ak-button__icon">account_circle</i> {{ values.email }} <i class="ak-icon m-l-5">expand_more</i>
                            </ak-button>
                        </div>

                        <ak-input-password
                            ref="password"
                            id="password"
                            v-model="values.password"
                            :hasError="errorMessage !== null"
                            :error="errorMessage"
                            :title="$t('Wachtwoord')"
                        />

                        <div class="ak-button-group">
                            <ak-button
                                :full-width="true"
                                @click="nextStep"
                            >
                                {{ $t('Ga verder') }}
                            </ak-button>
                        </div>
                    </div>

                    <div
                        v-if="step === 'tfa'"
                        key="tfa"
                        class="cms-login-form__step"
                    >
                        <div class="cms-login-form__email">
                            <ak-button
                                :disableBackground="true"
                                size="small"
                                @click="goToStep('email')"
                            >
                                <i class="ak-icon ak-button__icon">account_circle</i> {{ values.email }} <i class="ak-icon m-l-5">expand_more</i>
                            </ak-button>
                        </div>

                        <ak-input-text
                            ref="tfa"
                            id="tfa"
                            v-model="values.authenticationCode"
                            :hasError="errorMessage !== null"
                            :error="errorMessage"
                            :title="$t('Verificatie in 2 stappen')"
                            :helpText="$t('Een verificatiecode van de <b>Google Authenticator-app</b> ontvangen')"
                        />

                        <div class="ak-button-group">
                            <ak-button
                                :full-width="true"
                                @click="nextStep"
                            >
                                {{ $t('Ga verder') }}
                            </ak-button>
                        </div>
                    </div>
                </transition-group>

                <div v-if="sso && sso.enabled" class="cms-login-form__sso-group">
                    {{ $t('Of ga verder met:') }}
                    <div class="ak-button-group">
                        <ak-button
                            v-for="service in sso.services"
                            :key="service.id"
                            :title="service.description"
                            :icon="service.icon"
                            :href="service.href"
                            :outlined="true"
                            :full-width="true"
                            size="small"
                            :style="{'--button-background': service.color}"
                        >
                            <img v-if="service.logo" :src="service.logo" :alt="service.description" class="ak-button__img">
                            {{ service.description }}
                        </ak-button>
                    </div>
                </div>
            </div>

            <div class="cms-login-form__bottom">
                <ak-link
                    :disableBackground="true"
                    @click="passwordRecovery"
                >
                    {{ $t('Wachtwoord vergeten?') }}
                </ak-link>
            </div>
        </form>
    </div>
</template>

<script>
import {warehouseCredentials} from '../warehouse-credentials';

export default {
    data() {
        return {
            loading: false,
            steps: [
                'email',
                'password',
            ],
            savedAccounts: [],
            showManualLogin: false,
            storageWarning: null,
            validationRequestFailed: false,
            ssoFailed: false,
            errorMessage: null,
            step: 'email',
            sso: null,
            values: {
                email: null,
                password: null,
                authenticationCode: null,
            },
        };
    },
    computed: {
        showAccountChooser() {
            return this.savedAccounts.length > 0 && !this.showManualLogin;
        },
    },
    methods: {
        async nextStep() {
            if (this.loading) {
                return;
            }

            const valid = await this.validate(this.step);
            if (!valid) {
                return;
            }

            const indexOfCurrentStep = this.steps.indexOf(this.step);
            this.step = this.steps[indexOfCurrentStep + 1];
        },
        focusField() {
            this.$nextTick(() => {
                const field = this.$el.querySelector('#' + this.step);
                if (field) {
                    field.focus();
                }
            });
        },
        goToStep(step) {
            this.errorMessage = null;
            this.step = step;
            if (step === 'email') {
                this.showManualLogin = this.savedAccounts.length === 0;
                this.values.password = null;
                this.values.authenticationCode = null;
            }
        },
        async validate(step) {
            this.errorMessage = null;
            this.validationRequestFailed = false;
            this.loading = true;

            let result;
            try {
                result = await this.$post(this.$getSetting('paths.baseAuth') + '/user/validate-' + step, this.values);
            } catch (error) {
                this.loading = false;
                this.validationRequestFailed = true;
                this.errorMessage = this.$t('Aanmelden is momenteel niet mogelijk. Probeer het opnieuw.');
                return false;
            }

            this.loading = false;
            if (result.authenticated) {
                await this.authenticated(result);
                return result.success;
            }

            if (!result.success) {
                this.errorMessage = result.errorMessage;
                return result.success;
            }

            if (result.requireAuthenticationCode && !this.steps.includes('tfa')) {
                this.steps.push('tfa');
            }

            return result.success;
        },
        async authenticated(data) {
            this.$authService.setUser({name: data.user.firstName});
            this.loading = true;

            const displayName = [data.user.firstName, data.user.lastName]
                .filter(value => value)
                .join(' ') || this.values.email;

            try {
                await warehouseCredentials.save({
                    email: this.values.email,
                    password: this.values.password,
                    displayName,
                });
            } catch (error) {
                // Credential storage must never block a successful server login.
            }

            this.$actionHandler.handle(data.todos, this);
        },
        async loadSavedAccounts() {
            this.storageWarning = null;
            try {
                const result = await warehouseCredentials.list();
                if (!result.success) {
                    this.savedAccounts = [];
                    this.showManualLogin = true;
                    this.storageWarning = this.$t('Opgeslagen accounts zijn op dit toestel niet beschikbaar.');
                    return;
                }

                this.savedAccounts = result.accounts || [];
                if (!result.available) {
                    this.storageWarning = this.$t('Opgeslagen accounts zijn op dit toestel niet beschikbaar.');
                    this.showManualLogin = true;
                }
            } catch (error) {
                this.savedAccounts = [];
                this.showManualLogin = true;
            }
        },
        async loginSavedAccount(account) {
            this.errorMessage = null;
            this.loading = true;

            let result;
            try {
                result = await warehouseCredentials.get(account.email);
            } catch (error) {
                result = {success: false};
            }

            if (!result.success || !result.credential) {
                this.useStoredAccountManually(
                    account,
                    this.$t('De opgeslagen aanmeldgegevens konden niet worden geladen. Geef je huidige wachtwoord in.')
                );
                return;
            }

            this.values.email = result.credential.email;
            this.values.password = result.credential.password;
            this.values.authenticationCode = null;

            const valid = await this.validate('password');
            if (!valid) {
                if (this.validationRequestFailed) {
                    this.showManualLogin = true;
                    this.step = 'password';
                    this.values.password = null;
                    this.focusField();
                } else {
                    this.useStoredAccountManually(
                        account,
                        this.$t('Het opgeslagen wachtwoord is niet meer geldig. Geef je huidige wachtwoord in.')
                    );
                }
                return;
            }

            if (!this.$authService.isAuthenticated() && this.steps.includes('tfa')) {
                this.step = 'tfa';
                this.focusField();
            }
        },
        useStoredAccountManually(account, message) {
            this.loading = false;
            this.showManualLogin = true;
            this.step = 'password';
            this.values.email = account.email;
            this.values.password = null;
            this.values.authenticationCode = null;
            this.errorMessage = message;
            this.focusField();
        },
        useAnotherAccount() {
            this.showManualLogin = true;
            this.errorMessage = null;
            this.values.email = null;
            this.values.password = null;
            this.values.authenticationCode = null;
            this.focusField();
        },
        showSavedAccounts() {
            this.showManualLogin = false;
            this.errorMessage = null;
            this.values.email = null;
            this.values.password = null;
            this.values.authenticationCode = null;
        },
        async removeSavedAccount(account) {
            const confirmed = window.confirm(
                this.$t('Wil je dit account van dit toestel verwijderen?') + '\n\n' + account.email
            );
            if (!confirmed) {
                return;
            }

            try {
                const result = await warehouseCredentials.remove(account.email);
                if (result.success) {
                    await this.loadSavedAccounts();
                    this.showManualLogin = this.savedAccounts.length === 0;
                } else {
                    this.storageWarning = this.$t('Het account kon niet van dit toestel worden verwijderd.');
                }
            } catch (error) {
                this.storageWarning = this.$t('Het account kon niet van dit toestel worden verwijderd.');
            }
        },
        passwordRecovery() {
            this.$router.push({name: 'password-recovery'});
        },
    },
    async mounted() {
        await this.loadSavedAccounts();
        this.focusField();
    },
    created() {
        this.sso = this.$getSetting('sso');
        this.ssoFailed = (new URLSearchParams(window.location.search)).get('ssoFailed');
    },
};
</script>

<style lang="scss" scoped>
.warehouse-login-accounts {
    &__account {
        display: flex;
        width: 100%;
        margin-bottom: 10px;
        padding: 0;
        overflow: hidden;
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        background: var(--color-bg);
        color: var(--color-text);
    }

    &__identity {
        flex-grow: 1;
        padding: 10px;
        border: 0;
        background: transparent;
        color: var(--color-text);
        cursor: pointer;
        text-align: left;

        &:hover,
        &:focus {
            background: rgba(var(--color-1-rgb), .1);
            outline: none;
        }
    }

    &__name,
    &__email {
        display: block;
    }

    &__name {
        font-weight: bold;
    }

    &__email {
        margin-top: 2px;
        opacity: .7;
        font-size: .875em;
    }

    &__remove {
        padding: 10px;
        border: 0;
        border-left: 1px solid var(--border-color);
        background: transparent;
        color: var(--color-text);
        cursor: pointer;

        &:hover,
        &:focus {
            background: rgba(var(--color-danger-rgb), .1);
            color: var(--color-danger);
            outline: none;
        }
    }

    &__actions {
        margin-top: 10px;
        text-align: center;
    }
}
</style>
